import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Row,
  Col,
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Table,
  Modal,
  Select,
  Space,
  Typography,
  Tooltip,
  Tag,
  Popconfirm,
  message,
  Spin,
  Alert,
  Divider,
} from "antd";
import {
  SaveOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SettingOutlined,
  FireOutlined,
  TrophyOutlined,
  GiftOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { getCreditConfig, updateCreditConfig } from "@api/credit/credit";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Achievement trigger types
const TRIGGER_TYPES = [
  { value: "query_count", label: "Query Count", description: "Triggered after X queries" },
  { value: "copilot_usage", label: "Copilot Usage", description: "Triggered after using X copilots" },
  { value: "feature_usage", label: "Feature Usage", description: "Triggered after using X features" },
  { value: "referral", label: "Referral", description: "Triggered when user refers someone" },
  { value: "review", label: "Review", description: "Triggered when user leaves a review" },
  { value: "feedback", label: "Feedback", description: "Triggered when user submits feedback" },
  { value: "social_share", label: "Social Share", description: "Triggered when user shares on social media" },
  { value: "profile_complete", label: "Profile Complete", description: "Triggered when user completes profile" },
  { value: "days_active", label: "Days Active", description: "Triggered after X days of activity" },
  { value: "custom", label: "Custom", description: "Custom trigger logic" },
];

// Predefined badge options
const BADGE_OPTIONS = [
  "🌟", "🔥", "⭐", "🏆", "💎", "👑", "🎯", "💪", "🎓", "💬",
  "🤝", "🎁", "💫", "🏅", "🌈", "⚡", "🚀", "💡", "🎉", "✨"
];

// Section card wrapper
const SectionCard = ({ title, subtitle, icon, children, extra }) => (
  <Card
    style={{
      marginBottom: "24px",
      borderRadius: "16px",
      border: "1px solid #f0f0f0",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
    }}
    bodyStyle={{ padding: "24px" }}
  >
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "20px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{
          width: "40px",
          height: "40px",
          borderRadius: "10px",
          background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: "18px",
        }}>
          {icon}
        </div>
        <div>
          <Title level={5} style={{ margin: 0 }}>{title}</Title>
          {subtitle && <Text type="secondary" style={{ fontSize: "13px" }}>{subtitle}</Text>}
        </div>
      </div>
      {extra}
    </div>
    {children}
  </Card>
);

const CreditConfig = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [milestoneModalVisible, setMilestoneModalVisible] = useState(false);
  const [achievementModalVisible, setAchievementModalVisible] = useState(false);
  const [prizeModalVisible, setPrizeModalVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [editingPrize, setEditingPrize] = useState(null);
  const [milestoneForm] = Form.useForm();
  const [achievementForm] = Form.useForm();
  const [prizeForm] = Form.useForm();

  // Fetch config
  const { data: configData, isLoading, refetch } = useQuery(
    "creditConfig",
    async () => {
      const res = await getCreditConfig();
      return res.data?.data || res.data;
    },
    { staleTime: 30000 }
  );

  // Update config mutation
  const updateMutation = useMutation(
    async (values) => {
      const res = await updateCreditConfig(values);
      return res.data;
    },
    {
      onSuccess: () => {
        message.success("Credit configuration saved successfully!");
        queryClient.invalidateQueries("creditConfig");
      },
      onError: (error) => {
        message.error(error.response?.data?.message || "Failed to save configuration");
      },
    }
  );

  // Initialize form with config data
  useEffect(() => {
    if (configData) {
      form.setFieldsValue({
        enabled: configData.enabled ?? true,
        dailyCreditCap: configData.dailyCreditCap ?? 200,
        dailyLoginEnabled: configData.dailyLogin?.enabled ?? true,
        dailyLoginCredits: configData.dailyLogin?.credits ?? 5,
        streakEnabled: configData.streak?.enabled ?? true,
        gracePeriodHours: configData.streak?.gracePeriodHours ?? 36,
        achievementsEnabled: configData.achievements?.enabled ?? true,
        gamificationEnabled: configData.gamification?.enabled ?? true,
        cooldownHours: configData.gamification?.cooldownHours ?? 168,
      });
    }
  }, [configData, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const config = {
        enabled: values.enabled,
        dailyCreditCap: values.dailyCreditCap,
        dailyLogin: {
          enabled: values.dailyLoginEnabled,
          credits: values.dailyLoginCredits,
          resetHour: configData?.dailyLogin?.resetHour ?? 0,
        },
        streak: {
          enabled: values.streakEnabled,
          gracePeriodHours: values.gracePeriodHours,
          milestones: configData?.streak?.milestones || [],
        },
        achievements: {
          enabled: values.achievementsEnabled,
          list: configData?.achievements?.list || [],
        },
        gamification: {
          enabled: values.gamificationEnabled,
          cooldownHours: values.cooldownHours,
          games: configData?.gamification?.games || [],
        },
      };

      await updateMutation.mutateAsync(config);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  // Milestone handlers
  const handleAddMilestone = () => {
    setEditingMilestone(null);
    milestoneForm.resetFields();
    setMilestoneModalVisible(true);
  };

  const handleEditMilestone = (milestone, index) => {
    setEditingMilestone({ ...milestone, index });
    milestoneForm.setFieldsValue(milestone);
    setMilestoneModalVisible(true);
  };

  const handleSaveMilestone = async () => {
    try {
      const values = await milestoneForm.validateFields();
      const milestones = [...(configData?.streak?.milestones || [])];

      if (editingMilestone !== null && editingMilestone.index !== undefined) {
        milestones[editingMilestone.index] = values;
      } else {
        milestones.push(values);
      }

      // Sort by day
      milestones.sort((a, b) => a.day - b.day);

      const config = {
        ...configData,
        streak: {
          ...configData?.streak,
          milestones,
        },
      };

      await updateMutation.mutateAsync(config);
      setMilestoneModalVisible(false);
    } catch (error) {
      console.error("Save milestone failed:", error);
    }
  };

  const handleDeleteMilestone = async (index) => {
    const milestones = [...(configData?.streak?.milestones || [])];
    milestones.splice(index, 1);

    const config = {
      ...configData,
      streak: {
        ...configData?.streak,
        milestones,
      },
    };

    await updateMutation.mutateAsync(config);
  };

  // Achievement handlers
  const handleAddAchievement = () => {
    setEditingAchievement(null);
    achievementForm.resetFields();
    achievementForm.setFieldsValue({
      oneTime: true,
      enabled: true,
    });
    setAchievementModalVisible(true);
  };

  const handleEditAchievement = (achievement, index) => {
    setEditingAchievement({ ...achievement, index });
    achievementForm.setFieldsValue({
      ...achievement,
      triggerType: achievement.trigger?.type,
      triggerThreshold: achievement.trigger?.threshold,
    });
    setAchievementModalVisible(true);
  };

  const handleSaveAchievement = async () => {
    try {
      const values = await achievementForm.validateFields();
      const achievements = [...(configData?.achievements?.list || [])];

      const achievementData = {
        id: values.id || `achievement_${Date.now()}`,
        name: values.name,
        description: values.description,
        credits: values.credits,
        trigger: {
          type: values.triggerType,
          threshold: values.triggerThreshold,
        },
        oneTime: values.oneTime ?? true,
        badge: values.badge,
        enabled: values.enabled ?? true,
      };

      if (editingAchievement !== null && editingAchievement.index !== undefined) {
        achievements[editingAchievement.index] = achievementData;
      } else {
        achievements.push(achievementData);
      }

      const config = {
        ...configData,
        achievements: {
          ...configData?.achievements,
          list: achievements,
        },
      };

      await updateMutation.mutateAsync(config);
      setAchievementModalVisible(false);
    } catch (error) {
      console.error("Save achievement failed:", error);
    }
  };

  const handleDeleteAchievement = async (index) => {
    const achievements = [...(configData?.achievements?.list || [])];
    achievements.splice(index, 1);

    const config = {
      ...configData,
      achievements: {
        ...configData?.achievements,
        list: achievements,
      },
    };

    await updateMutation.mutateAsync(config);
  };

  const handleToggleAchievement = async (index, enabled) => {
    const achievements = [...(configData?.achievements?.list || [])];
    achievements[index] = { ...achievements[index], enabled };

    const config = {
      ...configData,
      achievements: {
        ...configData?.achievements,
        list: achievements,
      },
    };

    await updateMutation.mutateAsync(config);
  };

  // Prize handlers (for gamification)
  const handleAddPrize = () => {
    setEditingPrize(null);
    prizeForm.resetFields();
    prizeForm.setFieldsValue({ color: "#3b82f6" });
    setPrizeModalVisible(true);
  };

  const handleEditPrize = (prize, index) => {
    setEditingPrize({ ...prize, index });
    prizeForm.setFieldsValue(prize);
    setPrizeModalVisible(true);
  };

  const handleSavePrize = async () => {
    try {
      const values = await prizeForm.validateFields();
      const games = [...(configData?.gamification?.games || [])];

      // Get or create spin wheel game
      let spinWheelIndex = games.findIndex(g => g.type === "spin_wheel");
      if (spinWheelIndex === -1) {
        games.push({ type: "spin_wheel", enabled: true, prizes: [] });
        spinWheelIndex = games.length - 1;
      }

      const prizes = [...(games[spinWheelIndex].prizes || [])];

      const prizeData = {
        label: values.label,
        credits: values.credits,
        probability: values.probability,
        color: typeof values.color === "string" ? values.color : values.color?.toHexString?.() || "#3b82f6",
      };

      if (editingPrize !== null && editingPrize.index !== undefined) {
        prizes[editingPrize.index] = prizeData;
      } else {
        prizes.push(prizeData);
      }

      games[spinWheelIndex].prizes = prizes;

      const config = {
        ...configData,
        gamification: {
          ...configData?.gamification,
          games,
        },
      };

      await updateMutation.mutateAsync(config);
      setPrizeModalVisible(false);
    } catch (error) {
      console.error("Save prize failed:", error);
    }
  };

  const handleDeletePrize = async (index) => {
    const games = [...(configData?.gamification?.games || [])];
    const spinWheelIndex = games.findIndex(g => g.type === "spin_wheel");

    if (spinWheelIndex !== -1) {
      const prizes = [...(games[spinWheelIndex].prizes || [])];
      prizes.splice(index, 1);
      games[spinWheelIndex].prizes = prizes;

      const config = {
        ...configData,
        gamification: {
          ...configData?.gamification,
          games,
        },
      };

      await updateMutation.mutateAsync(config);
    }
  };

  // Calculate probability sum
  const getSpinWheelPrizes = () => {
    const game = configData?.gamification?.games?.find(g => g.type === "spin_wheel");
    return game?.prizes || [];
  };

  const probabilitySum = getSpinWheelPrizes().reduce((sum, p) => sum + (p.probability || 0), 0);
  const isProbabilityValid = Math.abs(probabilitySum - 100) < 0.01;

  // Milestone columns
  const milestoneColumns = [
    {
      title: "Day",
      dataIndex: "day",
      key: "day",
      width: 80,
      render: (day) => <Tag color="blue">{day}</Tag>,
    },
    {
      title: "Credits",
      dataIndex: "credits",
      key: "credits",
      width: 100,
      render: (credits) => (
        <Text strong style={{ color: "#10b981" }}>+{credits}</Text>
      ),
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Badge",
      dataIndex: "badge",
      key: "badge",
      width: 80,
      render: (badge) => <span style={{ fontSize: "20px" }}>{badge}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record, index) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditMilestone(record, index)}
          />
          <Popconfirm
            title="Delete this milestone?"
            onConfirm={() => handleDeleteMilestone(index)}
          >
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Achievement columns
  const achievementColumns = [
    {
      title: "Badge",
      dataIndex: "badge",
      key: "badge",
      width: 60,
      render: (badge) => <span style={{ fontSize: "20px" }}>{badge}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          {record.description && (
            <div><Text type="secondary" style={{ fontSize: "12px" }}>{record.description}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: "Credits",
      dataIndex: "credits",
      key: "credits",
      width: 100,
      render: (credits) => (
        <Text strong style={{ color: "#10b981" }}>+{credits}</Text>
      ),
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      render: (trigger) => {
        const triggerType = TRIGGER_TYPES.find(t => t.value === trigger?.type);
        return (
          <div>
            <Tag>{triggerType?.label || trigger?.type}</Tag>
            {trigger?.threshold && <Text type="secondary"> ({trigger.threshold})</Text>}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "enabled",
      key: "enabled",
      width: 80,
      render: (enabled, _, index) => (
        <Switch
          checked={enabled}
          size="small"
          onChange={(checked) => handleToggleAchievement(index, checked)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record, index) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditAchievement(record, index)}
          />
          <Popconfirm
            title="Delete this achievement?"
            onConfirm={() => handleDeleteAchievement(index)}
          >
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Prize columns
  const prizeColumns = [
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      width: 60,
      render: (color) => (
        <div
          style={{
            width: "24px",
            height: "24px",
            borderRadius: "6px",
            backgroundColor: color,
            border: "1px solid #e2e8f0",
          }}
        />
      ),
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Credits",
      dataIndex: "credits",
      key: "credits",
      width: 100,
      render: (credits) => (
        <Text strong style={{ color: credits > 0 ? "#10b981" : "#64748b" }}>
          {credits > 0 ? `+${credits}` : credits}
        </Text>
      ),
    },
    {
      title: "Probability",
      dataIndex: "probability",
      key: "probability",
      width: 120,
      render: (probability) => (
        <Tag color={probability > 20 ? "green" : probability > 5 ? "blue" : "orange"}>
          {probability}%
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record, index) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditPrize(record, index)}
          />
          <Popconfirm
            title="Delete this prize?"
            onConfirm={() => handleDeletePrize(index)}
          >
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{
        background: "#f8fafc",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "16px"
      }}>
        <Spin size="large" />
        <Text style={{ color: "#64748b" }}>Loading credit configuration...</Text>
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "calc(100vh - 80px)" }}>
      {/* Hero Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #10b981 100%)",
          padding: "32px 24px",
          marginBottom: "24px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{
          position: "absolute",
          top: "-50%",
          right: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />

        <Row align="middle" justify="space-between" style={{ position: "relative", zIndex: 1, maxWidth: "1400px", margin: "0 auto" }}>
          <Col xs={24} lg={16}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <div style={{
                width: "48px",
                height: "48px",
                borderRadius: "14px",
                background: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(10px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <SettingOutlined style={{ fontSize: "24px", color: "#fff" }} />
              </div>
              <h1 style={{ fontSize: "28px", fontWeight: 700, color: "#fff", margin: 0 }}>
                Credit Rewards Configuration
              </h1>
            </div>
            <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.9)", margin: 0 }}>
              Configure daily login, streak rewards, achievements, and gamification settings
            </p>
          </Col>
          <Col xs={24} lg={8} style={{ textAlign: "right", marginTop: window.innerWidth < 992 ? "20px" : 0 }}>
            <Space size="middle" wrap>
              <Button
                icon={<BarChartOutlined />}
                onClick={() => history.push("/super-admin/credits/analytics")}
                style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff" }}
              >
                View Analytics
              </Button>
              <Button
                icon={<ReloadOutlined spin={isLoading} />}
                onClick={() => refetch()}
                style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff" }}
              />
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={updateMutation.isLoading}
                style={{ background: "#fff", border: "none", color: "#f59e0b", fontWeight: 600 }}
              >
                Save Changes
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Content */}
      <div style={{ padding: "0 24px 24px", maxWidth: "1200px", margin: "0 auto" }}>
        <Form form={form} layout="vertical">
          {/* Global Settings */}
          <SectionCard
            title="Global Settings"
            subtitle="Enable or disable the entire credit rewards system"
            icon={<SettingOutlined />}
          >
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="enabled"
                  label="Enable Credit Rewards System"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="dailyCreditCap"
                  label={
                    <span>
                      Daily Credit Cap{" "}
                      <Tooltip title="Maximum credits a user can earn per day to prevent abuse">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  rules={[{ required: true, message: "Please enter daily credit cap" }]}
                >
                  <InputNumber
                    min={0}
                    max={10000}
                    style={{ width: "100%" }}
                    addonAfter="credits/user/day"
                  />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          {/* Daily Login */}
          <SectionCard
            title="Daily Login Bonus"
            subtitle="Award credits for first login each day"
            icon={<CalendarOutlined />}
          >
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="dailyLoginEnabled"
                  label="Enable Daily Login Bonus"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="dailyLoginCredits"
                  label="Credits per Login"
                  rules={[{ required: true, message: "Please enter credits amount" }]}
                >
                  <InputNumber
                    min={0}
                    max={1000}
                    style={{ width: "100%" }}
                    addonAfter="credits"
                  />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          {/* Streak Rewards */}
          <SectionCard
            title="Streak Rewards"
            subtitle="Bonus credits for consecutive daily logins"
            icon={<FireOutlined />}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddMilestone}
                style={{ background: "#8b5cf6" }}
              >
                Add Milestone
              </Button>
            }
          >
            <Row gutter={24} style={{ marginBottom: "20px" }}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="streakEnabled"
                  label="Enable Streak Rewards"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="gracePeriodHours"
                  label={
                    <span>
                      Grace Period{" "}
                      <Tooltip title="Hours before streak resets if user misses a day">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  rules={[{ required: true, message: "Please enter grace period" }]}
                >
                  <InputNumber
                    min={0}
                    max={72}
                    style={{ width: "100%" }}
                    addonAfter="hours"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Table
              dataSource={configData?.streak?.milestones || []}
              columns={milestoneColumns}
              rowKey={(record, index) => `milestone-${index}`}
              pagination={false}
              size="small"
              style={{ background: "#fafafa", borderRadius: "8px" }}
            />
          </SectionCard>

          {/* Achievements */}
          <SectionCard
            title="Achievements"
            subtitle="One-time rewards for reaching milestones"
            icon={<TrophyOutlined />}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddAchievement}
                style={{ background: "#8b5cf6" }}
              >
                Add Achievement
              </Button>
            }
          >
            <Row gutter={24} style={{ marginBottom: "20px" }}>
              <Col xs={24}>
                <Form.Item
                  name="achievementsEnabled"
                  label="Enable Achievements"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </Form.Item>
              </Col>
            </Row>

            <Table
              dataSource={configData?.achievements?.list || []}
              columns={achievementColumns}
              rowKey={(record, index) => record.id || `achievement-${index}`}
              pagination={false}
              size="small"
              style={{ background: "#fafafa", borderRadius: "8px" }}
            />
          </SectionCard>

          {/* Gamification */}
          <SectionCard
            title="Gamification"
            subtitle="Interactive games to earn bonus credits"
            icon={<GiftOutlined />}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddPrize}
                style={{ background: "#8b5cf6" }}
              >
                Add Prize
              </Button>
            }
          >
            <Row gutter={24} style={{ marginBottom: "20px" }}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="gamificationEnabled"
                  label="Enable Credit Games"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="cooldownHours"
                  label={
                    <span>
                      Cooldown Period{" "}
                      <Tooltip title="How often users can play (e.g., 168 = weekly)">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  rules={[{ required: true, message: "Please enter cooldown period" }]}
                >
                  <InputNumber
                    min={0}
                    max={720}
                    style={{ width: "100%" }}
                    addonAfter="hours"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider>Spin Wheel Prizes</Divider>

            {!isProbabilityValid && (
              <Alert
                type={probabilitySum === 0 ? "info" : "warning"}
                icon={probabilitySum === 0 ? <QuestionCircleOutlined /> : <WarningOutlined />}
                message={
                  probabilitySum === 0
                    ? "Add prizes to configure the spin wheel"
                    : `Probability sum is ${probabilitySum.toFixed(1)}% - must equal 100%`
                }
                style={{ marginBottom: "16px" }}
                showIcon
              />
            )}

            {isProbabilityValid && (
              <Alert
                type="success"
                icon={<CheckCircleOutlined />}
                message={`Probability sum: ${probabilitySum}%`}
                style={{ marginBottom: "16px" }}
                showIcon
              />
            )}

            <Table
              dataSource={getSpinWheelPrizes()}
              columns={prizeColumns}
              rowKey={(record, index) => `prize-${index}`}
              pagination={false}
              size="small"
              style={{ background: "#fafafa", borderRadius: "8px" }}
            />
          </SectionCard>
        </Form>
      </div>

      {/* Milestone Modal */}
      <Modal
        title={editingMilestone ? "Edit Streak Milestone" : "Add Streak Milestone"}
        open={milestoneModalVisible}
        onOk={handleSaveMilestone}
        onCancel={() => setMilestoneModalVisible(false)}
        confirmLoading={updateMutation.isLoading}
      >
        <Form form={milestoneForm} layout="vertical">
          <Form.Item
            name="day"
            label="Day"
            rules={[{ required: true, message: "Please enter the day number" }]}
          >
            <InputNumber min={1} max={365} style={{ width: "100%" }} placeholder="e.g., 7" />
          </Form.Item>
          <Form.Item
            name="credits"
            label="Credits"
            rules={[{ required: true, message: "Please enter credits amount" }]}
          >
            <InputNumber min={0} max={10000} style={{ width: "100%" }} placeholder="e.g., 25" />
          </Form.Item>
          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: "Please enter a label" }]}
          >
            <Input placeholder="e.g., Week Warrior!" />
          </Form.Item>
          <Form.Item
            name="badge"
            label="Badge"
            rules={[{ required: true, message: "Please select a badge" }]}
          >
            <Select placeholder="Select a badge">
              {BADGE_OPTIONS.map((badge) => (
                <Option key={badge} value={badge}>
                  <span style={{ fontSize: "18px", marginRight: "8px" }}>{badge}</span>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="notification" label="Custom Notification Message">
            <Input.TextArea rows={2} placeholder="Optional: Custom message shown to user" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Achievement Modal */}
      <Modal
        title={editingAchievement ? "Edit Achievement" : "Add Achievement"}
        open={achievementModalVisible}
        onOk={handleSaveAchievement}
        onCancel={() => setAchievementModalVisible(false)}
        confirmLoading={updateMutation.isLoading}
        width={600}
      >
        <Form form={achievementForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Please enter achievement name" }]}
              >
                <Input placeholder="e.g., Power User" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="credits"
                label="Credits"
                rules={[{ required: true, message: "Please enter credits" }]}
              >
                <InputNumber min={0} max={10000} style={{ width: "100%" }} placeholder="e.g., 50" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional: Description shown to user" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="triggerType"
                label="Trigger Type"
                rules={[{ required: true, message: "Please select trigger type" }]}
              >
                <Select placeholder="Select trigger">
                  {TRIGGER_TYPES.map((trigger) => (
                    <Option key={trigger.value} value={trigger.value}>
                      {trigger.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="triggerThreshold"
                label="Threshold (if applicable)"
              >
                <InputNumber min={1} style={{ width: "100%" }} placeholder="e.g., 100" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="badge"
                label="Badge"
                rules={[{ required: true, message: "Please select a badge" }]}
              >
                <Select placeholder="Select a badge">
                  {BADGE_OPTIONS.map((badge) => (
                    <Option key={badge} value={badge}>
                      <span style={{ fontSize: "18px", marginRight: "8px" }}>{badge}</span>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="oneTime" label="One-time" valuePropName="checked">
                <Switch defaultChecked />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="enabled" label="Enabled" valuePropName="checked">
                <Switch defaultChecked />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Prize Modal */}
      <Modal
        title={editingPrize ? "Edit Prize" : "Add Prize"}
        open={prizeModalVisible}
        onOk={handleSavePrize}
        onCancel={() => setPrizeModalVisible(false)}
        confirmLoading={updateMutation.isLoading}
      >
        <Form form={prizeForm} layout="vertical">
          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: "Please enter prize label" }]}
          >
            <Input placeholder="e.g., 100 Credits!" />
          </Form.Item>
          <Form.Item
            name="credits"
            label="Credits"
            rules={[{ required: true, message: "Please enter credits amount" }]}
          >
            <InputNumber min={0} max={10000} style={{ width: "100%" }} placeholder="e.g., 100" />
          </Form.Item>
          <Form.Item
            name="probability"
            label="Probability (%)"
            rules={[{ required: true, message: "Please enter probability" }]}
            extra="All probabilities must sum to 100%"
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: "100%" }}
              placeholder="e.g., 10"
              addonAfter="%"
            />
          </Form.Item>
          <Form.Item
            name="color"
            label="Color (hex)"
            rules={[{ required: true, message: "Please enter a color" }]}
          >
            <Input placeholder="#3B82F6" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CreditConfig;
